import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useLocation } from 'react-router-dom';
import type { StoreResponse } from '@clarity/contract';
import type { BrandResponse } from '@clarity/contract';
import { useScopeContext, type Scope } from '../lib/scopeContext.js';
import { lifecycleOf, type Lifecycle } from '../lib/lifecycle.js';
import { useI18n } from '../lib/i18n.js';
import { SURFACE_BY_ID, type SurfaceId } from '../features/nav.js';
import { IconSwitch } from './icons.js';

/** The translator shape from {@link useI18n}, narrowed for the helpers below. */
type Translate = ReturnType<typeof useI18n>['t'];

/**
 * The rail scope switcher (clarity-navigation-scope Req 2, design §6) — one
 * control that chooses what the whole workspace is pointed at: ALL stores, one
 * BRAND (its branches aggregated), or a single BRANCH. Modelled on the Talabat
 * partner store picker: a trigger button that shows the current scope + a
 * one-line summary, and a popover panel listing the choices.
 *
 * It reads everything from {@link useScopeContext} (the URL-derived scope) and
 * navigates with `goToScope`, which preserves the current surface where the
 * target scope allows it (Req 1.3) — so this component holds NO scope state of
 * its own beyond "is the panel open" and the search text; the URL stays the
 * source of truth (Req 1.4).
 *
 * Mounting into the rail is task 4.2; this file is the component + its tests in
 * isolation. The full glass styling + the final i18n sweep land in task 4.3 —
 * the strings used here are already in `lib/i18n` (EN + AR) so it is bilingual.
 */

/** Branches above this count get a search box (Req 2.3). */
const SEARCH_THRESHOLD = 7;

/**
 * One flat, selectable option in the panel — either the "All stores" row, a
 * brand group header (selectable AS the brand scope, Req 2.2/3.5), or a branch
 * row. Kept flat so the keyboard roving-focus walk is a simple index over the
 * visible options, while the render still nests branches under their brand head.
 */
type Option =
  | { key: string; kind: 'all'; scope: Scope }
  | { key: string; kind: 'brand'; scope: Scope; name: string; branchCount: number }
  | { key: string; kind: 'branch'; scope: Scope; store: StoreResponse };

/** True when `option` is the scope currently resolved from the URL. */
function isCurrent(option: Option, scope: Scope): boolean {
  if (option.scope.kind !== scope.kind) return false;
  if (option.scope.kind === 'brand' && scope.kind === 'brand') {
    return option.scope.brandId === scope.brandId;
  }
  if (option.scope.kind === 'store' && scope.kind === 'store') {
    return option.scope.storeId === scope.storeId;
  }
  return true; // both 'all'
}

/** The surface currently open, derived from the URL tail (for Req 1.3 preservation). */
function useCurrentSurface(): SurfaceId {
  const location = useLocation();
  const last = location.pathname.split('/').filter(Boolean).pop();
  return last && last in SURFACE_BY_ID ? (last as SurfaceId) : 'dashboard';
}

export function ScopeSwitcher() {
  const { t } = useI18n();
  const { scope, branches, brand, store, isSingleBranch, brandGroups, goToScope } =
    useScopeContext();
  const currentSurface = useCurrentSurface();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // The roving-focus cursor over the VISIBLE options while the panel is open.
  const [activeIndex, setActiveIndex] = useState(0);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const panelId = useId();
  const searchId = useId();

  // The total in-client branch count (across all groups) decides search + is the
  // "All stores" summary; `branches` is the IN-SCOPE set, so sum the groups.
  const allBranches = useMemo(
    () => brandGroups.flatMap((g) => g.branches),
    [brandGroups],
  );
  const showSearch = allBranches.length > SEARCH_THRESHOLD;

  const normalizedQuery = query.trim().toLowerCase();
  const matches = useCallback(
    (s: StoreResponse) =>
      !normalizedQuery ||
      (s.name ?? '').toLowerCase().includes(normalizedQuery) ||
      s.externalStoreId.toLowerCase().includes(normalizedQuery),
    [normalizedQuery],
  );

  // Build the flat, visible option list. "All stores" is always offered; a brand
  // group header is shown when ANY of its branches match the search (so a search
  // that hits a branch keeps its brand head), and each matching branch is a row.
  const options = useMemo<Option[]>(() => {
    const list: Option[] = [{ key: 'all', kind: 'all', scope: { kind: 'all' } }];
    for (const group of brandGroups) {
      const visibleBranches = group.branches.filter(matches);
      const brandMatches =
        !normalizedQuery || group.brand.name.toLowerCase().includes(normalizedQuery);
      // Keep a brand header when the brand name matches OR any of its branches do.
      if (!brandMatches && visibleBranches.length === 0) continue;
      list.push({
        key: `brand:${group.brand.id}`,
        kind: 'brand',
        scope: { kind: 'brand', brandId: group.brand.id },
        name: group.brand.name,
        branchCount: group.branches.length,
      });
      // When the brand name matched but no branch did, still show all its branches
      // so the group isn't an empty, un-expandable header.
      const branchesToShow = visibleBranches.length > 0 ? visibleBranches : group.branches;
      for (const b of branchesToShow) {
        list.push({ key: `store:${b.id}`, kind: 'branch', scope: { kind: 'store', storeId: b.id }, store: b });
      }
    }
    return list;
  }, [brandGroups, matches, normalizedQuery]);

  const hasBranches = allBranches.length > 0;
  // Only the "All stores" row survived an active search ⇒ nothing matched.
  const noMatch = normalizedQuery.length > 0 && options.every((o) => o.kind === 'all');

  // ── Open / close + focus management ────────────────────────────────────────
  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  // On open, point the roving cursor at the current scope's option (so the panel
  // opens focused on "where you are"), then move DOM focus to it after paint.
  useEffect(() => {
    if (!open) return;
    const currentIdx = options.findIndex((o) => isCurrent(o, scope));
    setActiveIndex(currentIdx >= 0 ? currentIdx : 0);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- open-edge only

  // Move focus to the active option whenever it changes while open (roving focus).
  useEffect(() => {
    if (!open) return;
    optionRefs.current[activeIndex]?.focus();
  }, [open, activeIndex, options.length]);

  // Click-outside closes the panel (Req 2.5).
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      close();
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, close]);

  function pick(option: Option) {
    goToScope(option.scope, currentSurface);
    close();
    triggerRef.current?.focus();
  }

  // Roving focus + activation over the flat option list (Req 2.5).
  function onPanelKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      triggerRef.current?.focus();
      return;
    }
    const last = options.length - 1;
    if (last < 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => (i >= last ? 0 : i + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => (i <= 0 ? last : i - 1));
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(last);
        break;
      default:
        break;
    }
  }

  // ── Trigger label + summary (Req 2.1) ───────────────────────────────────────
  const triggerName =
    scope.kind === 'all'
      ? t('scope.all')
      : scope.kind === 'brand'
        ? (brand?.name ?? t('scope.label'))
        : (store?.name ?? store?.externalStoreId ?? t('scope.label'));

  const triggerSummary = summarize({ scope, branches, brand, store, isSingleBranch, t });
  const triggerLifecycle = isSingleBranch && store ? lifecycleOf(store) : null;

  return (
    <div className="scope-switcher">
      <button
        ref={triggerRef}
        type="button"
        className="scope-switcher__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={`${t('scope.switch')}: ${triggerName}`}
        disabled={!hasBranches}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <span className="scope-switcher__trigger-text">
          <span className="scope-switcher__name">
            {hasBranches ? triggerName : t('scope.empty')}
          </span>
          <span className="scope-switcher__summary muted">
            {hasBranches ? triggerSummary : t('scope.empty.cta')}
          </span>
        </span>
        {triggerLifecycle && <LifecycleChip lifecycle={triggerLifecycle} />}
        <IconSwitch className="nav-ico scope-switcher__chevron" />
      </button>

      {!hasBranches && (
        // Empty state (Req 2.6): never a blank/crashing control — the disabled
        // trigger above shows "No branches yet", and this points to Brands.
        <p className="scope-switcher__empty muted" data-scope-empty>
          <a href="/brands">{t('scope.empty.cta')}</a>
        </p>
      )}

      {open && hasBranches && (
        <div
          ref={panelRef}
          id={panelId}
          className="scope-switcher__panel card"
          role="listbox"
          aria-label={t('scope.open')}
          onKeyDown={onPanelKeyDown}
        >
          {showSearch && (
            <div className="scope-switcher__search">
              <label htmlFor={searchId} className="sr-only">
                {t('scope.search')}
              </label>
              <input
                ref={searchRef}
                id={searchId}
                type="search"
                className="scope-switcher__search-input"
                placeholder={t('scope.search.placeholder')}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
              />
            </div>
          )}

          {noMatch && (
            <p className="scope-switcher__no-match muted">{t('scope.search.empty')}</p>
          )}
          {options.length > 0 && (
            <ul className="scope-switcher__list" role="none">
              {options.map((option, index) => {
                const current = isCurrent(option, scope);
                return (
                  <li key={option.key} role="none">
                    <button
                      ref={(el) => {
                        optionRefs.current[index] = el;
                      }}
                      type="button"
                      role="option"
                      aria-selected={current}
                      aria-current={current ? 'true' : undefined}
                      tabIndex={index === activeIndex ? 0 : -1}
                      className={optionClass(option, current)}
                      onClick={() => pick(option)}
                      onFocus={() => setActiveIndex(index)}
                    >
                      <OptionBody option={option} t={t} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}


/** The per-option CSS classes (brand headers vs branch rows vs the All row). */
function optionClass(option: Option, current: boolean): string {
  const base =
    option.kind === 'brand'
      ? 'scope-option scope-option--brand'
      : option.kind === 'branch'
        ? 'scope-option scope-option--branch'
        : 'scope-option scope-option--all';
  return current ? `${base} is-current` : base;
}

/** The visible content of one option row. */
function OptionBody({ option, t }: { option: Option; t: Translate }) {
  if (option.kind === 'all') {
    return <span className="scope-option__name">{t('scope.all')}</span>;
  }
  if (option.kind === 'brand') {
    const unit = option.branchCount === 1 ? t('scope.summary.branch') : t('scope.summary.branches');
    return (
      <>
        <span className="scope-option__name">{option.name}</span>
        <span className="scope-option__meta muted">
          {option.branchCount} {unit}
        </span>
      </>
    );
  }
  const { store } = option;
  const lifecycle = lifecycleOf(store);
  return (
    <>
      <span className="scope-option__name">{store.name ?? store.externalStoreId}</span>
      <span className="scope-option__meta muted">
        {store.externalStoreId} · {store.platformKey}
      </span>
      <LifecycleChip lifecycle={lifecycle} />
    </>
  );
}

/**
 * A lifecycle chip (Req 2.2) whose STATE is carried by text, not colour alone
 * (Req 2.5, WCAG AA) — the colour tokens are layered via `scope-chip--<state>`
 * in CSS, but the label itself names the state so a screen reader + a
 * colour-blind user both read it.
 */
function LifecycleChip({ lifecycle }: { lifecycle: Lifecycle }) {
  const { t } = useI18n();
  // A static lookup (not a template-literal key) keeps the i18n keys type-checked.
  const LABEL_KEY = {
    live: 'scope.lifecycle.live',
    onboarding: 'scope.lifecycle.onboarding',
    disabled: 'scope.lifecycle.disabled',
    removed: 'scope.lifecycle.removed',
  } as const;
  const label = t(LABEL_KEY[lifecycle]);
  return (
    <span
      className={`scope-chip scope-chip--${lifecycle}`}
      data-lifecycle={lifecycle}
      title={t('scope.lifecycle.aria', { label })}
    >
      {label}
    </span>
  );
}

/**
 * The one-line trigger summary (Req 2.1): e.g. "All stores · 3 branches",
 * "Burger Co · 2 branches · talabat", "Wadi El Seer · 780014". A single branch
 * reads as `externalStoreId`; an aggregate reads as a branch count (+ the brand's
 * platform when it is a brand scope, since a brand is bound to one platform).
 */
function summarize({
  scope,
  branches,
  brand,
  store,
  isSingleBranch,
  t,
}: {
  scope: Scope;
  branches: StoreResponse[];
  brand: BrandResponse | null;
  store: StoreResponse | null;
  isSingleBranch: boolean;
  t: Translate;
}): string {
  if (scope.kind === 'store' && store) {
    return store.externalStoreId;
  }
  // A one-branch brand scope reads as that branch, like the Talabat picker.
  if (scope.kind === 'brand' && isSingleBranch && branches[0]) {
    return branches[0].externalStoreId;
  }
  const count = branches.length;
  const unit =
    count === 1 ? t('scope.summary.branch') : t('scope.summary.branches');
  const parts = [`${count} ${unit}`];
  if (scope.kind === 'brand' && brand) {
    parts.push(brand.platformKey);
  }
  return parts.join(' · ');
}
